'use client';

import { invariant } from '@ifixit/helpers';
import { Slot } from '@radix-ui/react-slot';
import {
   type ComponentProps,
   type Dispatch,
   type ReactNode,
   type RefObject,
   type SetStateAction,
   createContext,
   use,
   useCallback,
   useId,
   useLayoutEffect,
   useMemo,
   useRef,
   useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useControllableState } from '../hooks/use-controllable-state';
import { useMounted } from '../hooks/use-mounted';
import { useStableCallback } from '../hooks/use-stable-callback';
import { afterExit } from '../lib/after-exit';
import { assignRef } from '../lib/assign-ref';
import { focusControl, focusFirst, tabbables } from '../lib/focus';
import { isSuspended, pushModal, removeModal, topModal } from '../lib/modal-stack';
import { lockPageScroll } from '../lib/scroll-lock';

/**
 * The parts Dialog, AlertDialog and Drawer share. The content is a native
 * <dialog> opened with showModal(): the browser keeps focus inside it, makes
 * the rest of the page inert and stacks it in the top layer, over any
 * z-index. Its ::backdrop is the dimmed overlay behind it.
 *
 * Anything that must stay usable while one is open renders inside it: popups
 * in place (Popover, Select, DropdownMenu, Tooltip), toasts through
 * AboveDialogs.
 */

type ModalRole = 'dialog' | 'alertdialog';

/**
 * Handed to `onInteractOutside` for a press outside the dialog, on its
 * backdrop. A touch reports the tap's click, not the press. preventDefault
 * keeps the dialog open.
 */
type ModalInteractOutsideEvent = CustomEvent<{ originalEvent: PointerEvent | MouseEvent }>;

interface ModalContentOptions {
   /** Runs on open, before focus moves into the dialog. preventDefault keeps focus where it is. */
   onOpenAutoFocus?: (event: Event) => void;
   /** Runs once the dialog has closed and the browser has returned focus to where it was before it opened. To send focus elsewhere, focus it here and preventDefault; otherwise focus the browser left on the page goes to the trigger. */
   onCloseAutoFocus?: (event: Event) => void;
   /** preventDefault keeps the dialog open. */
   onEscapeKeyDown?: (event: KeyboardEvent) => void;
   onInteractOutside?: (event: ModalInteractOutsideEvent) => void;
}

interface ModalLabels {
   titles: number;
   descriptions: number;
}

interface ModalContextValue {
   open: boolean;
   setOpen: (open: boolean) => void;
   modal: boolean;
   role: ModalRole;
   triggerRef: RefObject<HTMLElement | null>;
   /** Mounted: open, or closed but still playing its exit. */
   present: boolean;
   finishExit: () => void;
   contentId: string;
   titleId: string;
   descriptionId: string;
   labels: ModalLabels;
   setLabels: Dispatch<SetStateAction<ModalLabels>>;
}

const ModalContext = createContext<ModalContextValue | null>(null);

interface ModalRootProps {
   open?: boolean;
   defaultOpen?: boolean;
   onOpenChange?: (open: boolean) => void;
   /**
    * False renders the portal's children while open and nothing more: no
    * <dialog>, no focus or dismiss handling. The caller owns all of it.
    */
   modal?: boolean;
   role?: ModalRole;
   children?: ReactNode;
}

export function ModalRoot({
   open: openProp,
   defaultOpen = false,
   onOpenChange,
   modal = true,
   role = 'dialog',
   children,
}: ModalRootProps) {
   // The hook's own onChange only fires in controlled mode; setOpen below
   // notifies uncontrolled consumers too.
   const [open, setOpenState] = useControllableState({ prop: openProp, defaultProp: defaultOpen });
   const setOpen = useStableCallback((next: boolean) => {
      if (next === open) return;
      setOpenState(next);
      onOpenChange?.(next);
   });
   const [present, setPresent] = useState(open);
   if (open && !present) setPresent(true);
   const finishExit = useCallback(() => setPresent(false), []);

   const triggerRef = useRef<HTMLElement | null>(null);
   const contentId = useId();
   const titleId = useId();
   const descriptionId = useId();
   const [labels, setLabels] = useState<ModalLabels>({ titles: 0, descriptions: 0 });

   const context = useMemo(
      () => ({
         open,
         setOpen,
         modal,
         role,
         triggerRef,
         present: modal ? present : open,
         finishExit,
         contentId,
         titleId,
         descriptionId,
         labels,
         setLabels,
      }),
      [open, setOpen, modal, role, present, finishExit, contentId, titleId, descriptionId, labels]
   );
   return <ModalContext value={context}>{children}</ModalContext>;
}

interface ButtonProps extends ComponentProps<'button'> {
   asChild?: boolean;
}

export function ModalTrigger({ asChild = false, onClick, ref, ...props }: ButtonProps) {
   const { open, setOpen, contentId, triggerRef } = useModal();
   const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
         triggerRef.current = node;
         assignRef(ref, node);
      },
      [triggerRef, ref]
   );
   const Comp = asChild ? Slot : 'button';
   return (
      <Comp
         ref={setRefs}
         type="button"
         aria-haspopup="dialog"
         aria-expanded={open}
         aria-controls={open ? contentId : undefined}
         data-state={open ? 'open' : 'closed'}
         onClick={event => {
            onClick?.(event);
            if (!event.defaultPrevented) setOpen(!open);
         }}
         {...props}
      />
   );
}

export function ModalClose({ asChild = false, onClick, ...props }: ButtonProps) {
   const { setOpen } = useModal();
   const Comp = asChild ? Slot : 'button';
   return (
      <Comp
         type="button"
         onClick={event => {
            onClick?.(event);
            if (!event.defaultPrevented) setOpen(false);
         }}
         {...props}
      />
   );
}

/**
 * Renders the dialog at the end of <body>, as Radix did, so it inherits the
 * body's text styles wherever it is opened from.
 */
export function ModalPortal({ children }: { children?: ReactNode }) {
   const { present } = useModal();
   const isMounted = useMounted();
   if (!present || !isMounted) return null;
   return createPortal(children, document.body);
}

interface ModalContentProps extends Omit<ComponentProps<'dialog'>, 'open'>, ModalContentOptions {}

export function ModalContent({
   ref,
   children,
   onOpenAutoFocus,
   onCloseAutoFocus,
   onEscapeKeyDown,
   onInteractOutside,
   ...props
}: ModalContentProps) {
   const { open, modal, role, contentId, titleId, descriptionId, labels } = useModal();
   invariant(modal, 'Dialog content needs a modal dialog');
   const dialogRef = useRef<HTMLDialogElement | null>(null);
   const setRefs = useCallback(
      (node: HTMLDialogElement | null) => {
         dialogRef.current = node;
         assignRef(ref, node);
      },
      [ref]
   );

   // Read by the effects below on open and on close, with the latest props.
   const optionsRef = useRef<ModalContentOptions>({});
   useLayoutEffect(() => {
      optionsRef.current = {
         onOpenAutoFocus,
         onCloseAutoFocus,
         onEscapeKeyDown,
         onInteractOutside,
      };
   });

   const isShown = useShowModal(dialogRef, optionsRef);
   useDismissal(dialogRef, optionsRef);
   useTabLoop(dialogRef);

   return (
      <dialog
         ref={setRefs}
         id={contentId}
         role={role === 'alertdialog' ? 'alertdialog' : undefined}
         aria-labelledby={labels.titles > 0 ? titleId : undefined}
         aria-describedby={labels.descriptions > 0 ? descriptionId : undefined}
         tabIndex={-1}
         data-state={open ? 'open' : 'closed'}
         {...props}
      >
         {/* Mounted once the dialog is shown: a part that focuses itself on
             mount (autoFocus) can only take focus in a displayed dialog. */}
         {isShown ? children : null}
      </dialog>
   );
}

type LabelProps<Element extends 'h2' | 'p'> = ComponentProps<Element> & { asChild?: boolean };

export function ModalTitle({ asChild = false, ...props }: LabelProps<'h2'>) {
   const { titleId } = useModal();
   useRegisterLabel('titles');
   const Comp = asChild ? Slot : 'h2';
   return <Comp id={titleId} {...props} />;
}

export function ModalDescription({ asChild = false, ...props }: LabelProps<'p'>) {
   const { descriptionId } = useModal();
   useRegisterLabel('descriptions');
   const Comp = asChild ? Slot : 'p';
   return <Comp id={descriptionId} {...props} />;
}

export function useModal() {
   const context = use(ModalContext);
   invariant(context, 'Dialog parts must be rendered inside their dialog');
   return context;
}

/**
 * Shows the dialog as a modal on open, and says when it is shown for the
 * children to mount. On close the dialog and its backdrop play their exit
 * first; then it closes, which hands focus back to where it was before it
 * opened, and it unmounts.
 */
function useShowModal(
   dialogRef: RefObject<HTMLDialogElement | null>,
   optionsRef: RefObject<ModalContentOptions>
) {
   const { open, finishExit, triggerRef } = useModal();
   const [isShown, setIsShown] = useState(false);
   const releaseScrollRef = useRef<(() => void) | null>(null);

   const settle = useStableCallback(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      const wasOpen = dialog.open;
      if (wasOpen) dialog.close();
      removeModal(dialog);
      releaseScrollRef.current?.();
      releaseScrollRef.current = null;
      if (!wasOpen) return;
      const event = dispatchOn(
         dialog,
         new CustomEvent('dialog.closeAutoFocus', { cancelable: true }),
         optionsRef.current.onCloseAutoFocus
      );
      // Safari doesn't focus a button on click, so a dialog opened with one
      // returns focus to the page, or leaves it on the closed dialog until
      // the next frame. Radix focused the trigger itself.
      const focused = document.activeElement;
      const isStranded = focused === null || focused === document.body || dialog.contains(focused);
      const trigger = triggerRef.current;
      if (!event.defaultPrevented && isStranded && trigger?.isConnected) {
         trigger.focus({ preventScroll: true });
      }
   });

   useLayoutEffect(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      if (!open) {
         return afterExit(
            dialog,
            () => {
               settle();
               finishExit();
            },
            { pseudoElements: true }
         );
      }
      // Reopened before its exit finished: still shown, focus still inside.
      if (dialog.open) return;
      // The browser focuses the dialog itself rather than its first field,
      // which on a phone would raise the keyboard before onOpenAutoFocus runs.
      dialog.setAttribute('autofocus', '');
      dialog.showModal();
      pushModal(dialog);
      releaseScrollRef.current = lockPageScroll();
      setIsShown(true);
   }, [open, dialogRef, finishExit, settle]);

   // The children have mounted. As with Radix, one that took focus on mount
   // keeps it; otherwise onOpenAutoFocus runs and focus moves in.
   useLayoutEffect(() => {
      const dialog = dialogRef.current;
      if (!isShown || !dialog) return;
      const focused = document.activeElement;
      if (focused !== dialog && dialog.contains(focused)) return;
      const event = dispatchOn(
         dialog,
         new CustomEvent('dialog.openAutoFocus', { cancelable: true }),
         optionsRef.current.onOpenAutoFocus
      );
      if (!event.defaultPrevented) focusFirst(dialog, ownTabbables(dialog));
   }, [isShown, dialogRef, optionsRef]);

   // Unmounted without its exit (the caller dropped the whole dialog): close
   // it before React removes it, so focus still goes back and the page is
   // released.
   useLayoutEffect(() => settle, [settle]);

   return isShown;
}

/**
 * Escape, and a press on the backdrop, close the dialog, each after its
 * callback had the chance to keep it open.
 */
function useDismissal(
   dialogRef: RefObject<HTMLDialogElement | null>,
   optionsRef: RefObject<ModalContentOptions>
) {
   const { setOpen } = useModal();

   useLayoutEffect(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      // A popup open in the dialog listens on window and marks the key
      // handled, so it closes first. The document's capture phase comes
      // before the sheets and composers that listen on the document, as
      // Radix's did, and marking the key handled keeps them open too.
      function handleKeyDown(event: KeyboardEvent) {
         if (event.key === 'Escape' && !isAboveDialogs(event.target)) closeOnEscape(event);
      }
      // A toast gets its Escape first, and closes itself; one it leaves
      // unhandled bubbles up here.
      function handleAboveDialogsKeyDown(event: KeyboardEvent) {
         if (event.key === 'Escape' && isAboveDialogs(event.target)) closeOnEscape(event);
      }
      function closeOnEscape(event: KeyboardEvent) {
         if (event.isComposing || event.defaultPrevented) return;
         if (!dialog || topModal() !== dialog) return;
         optionsRef.current.onEscapeKeyDown?.(event);
         if (event.defaultPrevented) return;
         // Also stops the browser closing the dialog itself, which would
         // skip the exit.
         event.preventDefault();
         setOpen(false);
      }
      // A close request other than Escape, like Android's back button.
      function handleCancel(event: Event) {
         if (event.target !== dialog) return;
         event.preventDefault();
         setOpen(false);
      }
      // The browser closed it anyway: it refuses a second close request that
      // comes without the user interacting in between. The event arrives
      // after close() returns, so one for a dialog shown again since (an
      // effect run twice, as StrictMode does) is stale.
      function handleClose() {
         if (!dialog?.open) setOpen(false);
      }

      function dismissOutside(originalEvent: PointerEvent | MouseEvent) {
         if (!dialog || !isOnBackdrop(dialog, originalEvent)) return;
         const event = dispatchOn(
            dialog,
            new CustomEvent('dialog.interactOutside', {
               cancelable: true,
               detail: { originalEvent },
            }),
            optionsRef.current.onInteractOutside
         );
         if (!event.defaultPrevented) setOpen(false);
      }
      // A touch closes on the tap's click, as Radix did: the press alone may
      // start a scroll.
      function handlePointerDown(event: PointerEvent) {
         document.removeEventListener('click', dismissOutside, { capture: true });
         // Radix's exception: a right click, or a Mac's Ctrl+click, opens the
         // context menu and leaves the dialog open.
         if (event.button === 2 || (event.button === 0 && event.ctrlKey)) return;
         if (event.pointerType !== 'touch') {
            dismissOutside(event);
         } else if (dialog && isOnBackdrop(dialog, event)) {
            document.addEventListener('click', dismissOutside, { capture: true, once: true });
         }
      }

      document.addEventListener('keydown', handleKeyDown, { capture: true });
      dialog.addEventListener('keydown', handleAboveDialogsKeyDown);
      dialog.addEventListener('cancel', handleCancel);
      dialog.addEventListener('close', handleClose);
      dialog.addEventListener('pointerdown', handlePointerDown, { capture: true });
      return () => {
         document.removeEventListener('keydown', handleKeyDown, { capture: true });
         dialog.removeEventListener('keydown', handleAboveDialogsKeyDown);
         dialog.removeEventListener('cancel', handleCancel);
         dialog.removeEventListener('close', handleClose);
         dialog.removeEventListener('pointerdown', handlePointerDown, { capture: true });
         document.removeEventListener('click', dismissOutside, { capture: true });
      };
   }, [dialogRef, optionsRef, setOpen]);
}

/**
 * Radix's loop: Tab past the last control wraps to the first and Shift+Tab
 * back, rather than leaving for the browser's own controls.
 */
function useTabLoop(dialogRef: RefObject<HTMLDialogElement | null>) {
   useLayoutEffect(() => {
      const dialog = dialogRef.current;
      if (!dialog) return;
      function loopTab(event: KeyboardEvent) {
         if (event.key !== 'Tab' || event.altKey || event.ctrlKey || event.metaKey) return;
         // A popover in the dialog loops its own Tab.
         if (event.defaultPrevented || !dialog || isSuspended(dialog)) return;
         const focused = document.activeElement;
         // A toast above the dialog tabs through its own controls.
         if (isAboveDialogs(focused)) return;
         const tabbable = ownTabbables(dialog);
         const first = tabbable[0];
         const last = tabbable.at(-1);
         if (!first || !last) {
            event.preventDefault();
         } else if (!event.shiftKey && focused === last) {
            event.preventDefault();
            focusControl(first);
         } else if (event.shiftKey && (focused === first || focused === dialog)) {
            event.preventDefault();
            focusControl(last);
         }
      }
      dialog.addEventListener('keydown', loopTab);
      return () => dialog.removeEventListener('keydown', loopTab);
   }, [dialogRef]);
}

function useRegisterLabel(kind: keyof ModalLabels) {
   const { setLabels } = useModal();
   useLayoutEffect(() => {
      setLabels(labels => ({ ...labels, [kind]: labels[kind] + 1 }));
      return () => setLabels(labels => ({ ...labels, [kind]: labels[kind] - 1 }));
   }, [kind, setLabels]);
}

// Toasts render in the newest dialog (AboveDialogs) without being part of
// it: they don't take its first focus or its Tab loop, and they get Escape
// before it does.
function isAboveDialogs(target: EventTarget | null) {
   return target instanceof Element && target.closest('[data-above-dialogs]') !== null;
}

function ownTabbables(dialog: HTMLDialogElement) {
   return tabbables(dialog).filter(element => !isAboveDialogs(element));
}

// A press on the backdrop reaches the dialog itself, outside its box. One on
// the dialog's own padding or border lands inside the box, and one in a popup
// or a toast inside the dialog has another target.
function isOnBackdrop(dialog: HTMLDialogElement, event: PointerEvent | MouseEvent) {
   if (event.target !== dialog) return false;
   const box = dialog.getBoundingClientRect();
   return (
      event.clientX < box.left ||
      event.clientX > box.right ||
      event.clientY < box.top ||
      event.clientY > box.bottom
   );
}

// Radix dispatched its events on the dialog with the callback attached, so a
// callback reads the dialog as event.currentTarget.
function dispatchOn<T extends Event>(
   target: EventTarget,
   event: T,
   listener: ((event: T) => void) | undefined
) {
   const handle = (dispatched: Event) => listener?.(dispatched as T);
   target.addEventListener(event.type, handle);
   target.dispatchEvent(event);
   target.removeEventListener(event.type, handle);
   return event;
}
